import React, { useState } from 'react';
import commonStyles from "../../../style/common.module.css";
import styles from "../../style/assignment.module.css";
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { BadgeAssignment, isEvaluatorEditable } from '../../model/badge-assignment';
import dayjs from 'dayjs';
import { getNumber, isValidHttpUrl } from '../../../../utils/helper/string-helper';
import BaseForm from '../../../../component/form/base-form';
import { updateEvaluatorAssignment } from '../../service/assignment-service';
import { useToast } from '../../../../context/toast/toast-provider';
import { evaluatorUpdateFieldDefinitions } from './evaluator-update-field-definition';
import MenuAction from '../../../../component/menu/menu-action';
import { CourseLevel } from '../../../-education/enum/course-level';
import { getBadgeTerminology } from '../../utils/terminology';
import PresentationViewer from '../../../../component/presentation/presentation-viewer';

export default function EvaluatorJournalBadgeContent({
    initialBadge,
    updateBadge,
    moduleCourseLevel
}: {
    initialBadge: BadgeAssignment,
    updateBadge(badgeAssignment: BadgeAssignment): void,
    moduleCourseLevel: CourseLevel
}) {
    const [submitting, setSubmitting] = useState(false);
    const [editActive, setEditActive] = useState<boolean>(false);
    const [badge, setBadge] = useState<BadgeAssignment>(initialBadge);

    const toast = useToast();

    const submissionUrl = isValidHttpUrl(initialBadge.workingBadgePath)
        ? initialBadge.workingBadgePath?.trim()
        : undefined;

    var currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const submitValidation = (badgeAssignment: BadgeAssignment, errors: any) => {
        const commentGiven = (badgeAssignment.comment !== undefined && badgeAssignment.comment !== '');
        const ratingGiven = (badgeAssignment.rating !== undefined && badgeAssignment.rating !== '' && Number(badgeAssignment.rating) !== 0);

        if (!commentGiven && !ratingGiven) {
            return {
                ...errors,
                rating: `Please provide either a comment or set a review rating`,
                comment: `Please provide either a comment or set a review rating`
            };
        }
        return errors
    }

    const handleSubmit = (badgeAssignment: BadgeAssignment) => {
        setSubmitting(true);

        badgeAssignment.rating = badgeAssignment.rating?.toString();
        if (badgeAssignment.comment !== undefined && badgeAssignment.comment.length > 0) {
            badgeAssignment.comments = [...(badgeAssignment.comments ?? [])];
            badgeAssignment.comments.push(`<ec>${badgeAssignment.comment}`);
        }
        updateEvaluatorAssignment(badgeAssignment).then(result => {
            toast.addToast(`Great work! ${getBadgeTerminology(moduleCourseLevel, false)} ${result?.badgeName} submission review has been updated`, "success");
            if (result !== undefined) {
                result.moduleId = badge.moduleId;
                updateBadge(result);
            }

            setBadge({...result, comment: undefined});
            setEditActive(false);
            setSubmitting(false);
        }).catch(() => {
            toast.addToast(`Unable to update ${getBadgeTerminology(moduleCourseLevel)} ${badgeAssignment.badgeName} submission review`, "error");
            setEditActive(false);
            setSubmitting(false);
        });
    }

    return (
        <React.Fragment>
            <div className={styles.journal} >
                <Typography>
                    {initialBadge.submissionDate !== undefined && <span className={commonStyles.smr}><b>Submission date:</b> {dayjs(initialBadge.submissionDate).format('YYYY-MM-DD')}</span>}
                    {initialBadge.submissionDate !== undefined && initialBadge.evaluatedDate !== undefined && <br />}
                    {initialBadge.evaluatedDate !== undefined && <span className={commonStyles.smr}><b>Evaluation date:</b> {dayjs(initialBadge.evaluatedDate).format('YYYY-MM-DD')}</span>}
                </Typography>
                {isEvaluatorEditable(initialBadge) && <Typography>
                    <MenuAction handleEdit={() => setEditActive(!editActive) } />
                </Typography>}
            </div>
            {editActive === false && getNumber(initialBadge.rating) > 0 && <Typography className={commonStyles.pb} sx={{ display: 'flex' }} >
                <b>Your rating: </b><span className={commonStyles.sml} >{Array.from({ length: getNumber(initialBadge.rating) }, () => <StarIcon fontSize="small" key={`${Math.random()}-select-icon`} color="primary" />)}</span>
            </Typography>}
            <Typography className={commonStyles.pb}>
                <b>{getBadgeTerminology(moduleCourseLevel)} instruction: </b><br />
                {initialBadge.badgeInstruction !== undefined && initialBadge.badgeInstruction !== '' && <span style={{ overflowWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: initialBadge.badgeInstruction }}>
                </span>}
                {(initialBadge.badgeInstruction === undefined || initialBadge.badgeInstruction === '') && <span style={{ overflowWrap: 'break-word', color: 'lightgrey' }}>
                    <i>{getBadgeTerminology(moduleCourseLevel)} instruction missing</i>
                </span>}
            </Typography>
            <Typography className={commonStyles.pb}>
                <b>{getBadgeTerminology(moduleCourseLevel)} acceptance criteria: </b><br />
                {initialBadge.acceptanceCriteria !== undefined && initialBadge.acceptanceCriteria !== '' && <span style={{ overflowWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: initialBadge.acceptanceCriteria }}>
                </span>}
                {(initialBadge.acceptanceCriteria === undefined || initialBadge.acceptanceCriteria === '') && <span style={{ overflowWrap: 'break-word', color: 'lightgrey' }}>
                    <i>{getBadgeTerminology(moduleCourseLevel)} acceptance criteria missing</i>
                </span>}
            </Typography>
            <Box className={commonStyles.pb}>
                <b>Resources:</b>
                <PresentationViewer
                    badgeName={initialBadge.badgeName ?? 'Badge'}
                    presentationUrl={initialBadge.presentationUrl}
                    sessionMapUrl={initialBadge.sessionMapUrl}
                    submissionUrl={submissionUrl}
                    showSubmission={true}
                />
            </Box>
            <Typography className={commonStyles.pb}>
                <b>Comments: </b><br />
                {initialBadge.comments !== undefined && initialBadge.comments.length > 0 && <span style={{ overflowWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: initialBadge.comments.join('<br />').replaceAll('<ec>', 'You: ').replaceAll('<sc>', 'Student: ') }}>
                </span>}
                {(initialBadge.comments === undefined || initialBadge.comments.length === 0) && <span style={{ overflowWrap: 'break-word', color: 'lightgrey' }}>
                    <i>No comment given</i>
                </span>}
            </Typography>
            {editActive === true && <div className={commonStyles.pt} >
                <BaseForm
                    model={badge}
                    setModel={setBadge}
                    fields={evaluatorUpdateFieldDefinitions}
                    submitValidation={submitValidation}
                    submitEntity={handleSubmit}
                    submitting={submitting}
                    submitButtonText='Update'
                    cancel={() => {
                        setEditActive(false);
                    }}
                />
            </div>}
        </React.Fragment>
    );
}